// Fattura di cortesia di un ordine Metro: riscarico del PDF e caricamento a
// mano sul marketplace (spec 2026-08-03-pdf-fattura-metro-design.md).
//
// Il caricamento vero lo fa il giro fatturazione schedulato. Questi due tasti
// servono quando serve SUBITO: Metro contesta e vuoi il PDF in mano, oppure
// l'ordine è stato spedito dopo l'emissione e non si vuole aspettare la
// ripassata.
//
// L'esito vive SOLO qui, in memoria, e non in una colonna: il backend non
// conserva lo stato del caricamento perché la fonte di verità è Metro
// (`line.documents`), non una nostra tabella — ricaricare due volte è già un
// no-op. Quindi «l'ultimo esito noto» è quello del click appena fatto.
import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { caricaFatturaSuMetro, scaricaPdfFattura } from "../api";
import type { EsitoMetroUpload, Proposal } from "../types";
import { risolviCanale } from "./ChannelCell";
import { ACTION_BTN } from "./columns";

/**
 * L'insegna della riga è Metro, **oppure non è ricavabile** (canale vuoto,
 * `generic`, insegna sconosciuta): in quel secondo caso le azioni si mostrano
 * lo stesso e a dire di no è il backend, che il canale lo deduce dal
 * riferimento d'ordine e risponde 409 con la frase già pronta. Indovinare qui
 * il pattern dei riferimenti vorrebbe dire tenere allineate due regole.
 */
export function forseMetro(channel: string): boolean {
  const { insegna } = risolviCanale(channel);
  return insegna === "metro" || insegna === "" || insegna === "generic" || insegna === "unknown";
}

const MOTIVO_SENZA_TENTATIVO =
  "Il PDF si stampa dalla risposta del gestionale conservata nel diario di emissione, " +
  "e per questa riga non c'è: il documento non è stato emesso dal portale (numero dedotto " +
  "dal registro, import storico o riga segnata a mano).";

/** «FA n.67», o il solo numero se la sigla manca. */
function etichettaDoc(r: Proposal): string {
  return `${r.doc_type ? `${r.doc_type} ` : ""}n.${r.document_number}`;
}

/** «1 riga» / «3 righe»: mai il «2 rigae» dei pezzi incollati. */
const righe = (n: number): string => (n === 1 ? "1 riga" : `${n} righe`);

/**
 * Esito del caricamento in una frase italiana.
 *
 * Non si usa la sola `descrizione` del backend: quando alcune righe sono state
 * caricate e altre no, quella racconta solo le caricate — e il pezzo che manca
 * è proprio quello che dice di riprovare.
 */
export function messaggioEsito(e: EsitoMetroUpload, doc: string): string {
  const pezzi: string[] = [];
  if (e.caricate.length > 0) pezzi.push(`caricata su ${righe(e.caricate.length)}`);
  if (e.gia_presenti.length > 0) pezzi.push(`già presente su ${righe(e.gia_presenti.length)}`);
  const testa =
    pezzi.length > 0
      ? `Fattura ${doc} ${pezzi.join(", ")}.`
      : `Fattura ${doc}: nessuna riga da caricare su Metro.`;
  if (e.completato) return testa;
  return (
    `${testa} ${righe(e.non_spedite.length)} non ancora spedite da Metro: ` +
    "non è un errore, il caricamento si completa da solo al giro successivo (o riprova più tardi)."
  );
}

/**
 * Azioni sul documento emesso di un ordine Metro. Rende `null` — cioè non
 * occupa niente nella cella — quando la riga non ha un numero documento o il
 * canale è sicuramente un altro.
 */
export function MetroInvoiceActions({
  proposal,
  onEsito,
  onErrore,
}: {
  proposal: Proposal;
  /** Esito positivo o «in attesa»: va nella riga di stato della pagina. */
  onEsito: (messaggio: string) => void;
  /** Errore vero, già in italiano: va nel banner rosso della pagina. */
  onErrore: (messaggio: string) => void;
}) {
  const [busy, setBusy] = useState<"pdf" | "metro" | null>(null);
  const [esito, setEsito] = useState<EsitoMetroUpload | null>(null);

  if (!proposal.document_number || !forseMetro(proposal.channel)) return null;

  const attemptId = proposal.document_attempt_id ?? null;
  const doc = etichettaDoc(proposal);

  async function esegui(cosa: "pdf" | "metro") {
    // `attemptId` ricontrollato qui e non solo nel `disabled`: il tasto spento
    // è cosmesi, questa è la guardia.
    if (busy !== null || attemptId === null) return;
    setBusy(cosa);
    try {
      if (cosa === "pdf") {
        await scaricaPdfFattura(attemptId);
        onEsito(`PDF della fattura ${doc} scaricato.`);
      } else {
        const risultato = await caricaFatturaSuMetro(attemptId);
        setEsito(risultato);
        onEsito(messaggioEsito(risultato, doc));
      }
    } catch (err) {
      // 404 (niente da stampare), 409 (non è un ordine Metro) e 502 (Metro non
      // risponde) arrivano dal backend già in italiano: `prettifyApiError`
      // toglie il prefisso tecnico e non mostra mai il codice HTTP nudo.
      onErrore(prettifyApiError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy !== null || attemptId === null}
        onClick={() => void esegui("pdf")}
        className={ACTION_BTN}
        title={
          attemptId === null
            ? MOTIVO_SENZA_TENTATIVO
            : "Scarica il PDF della fattura di cortesia: è lo stesso file che viene caricato su Metro"
        }
      >
        {busy === "pdf" ? "Scarico…" : "Scarica PDF"}
      </button>
      <button
        type="button"
        disabled={busy !== null || attemptId === null}
        onClick={() => void esegui("metro")}
        className={ACTION_BTN}
        title={
          attemptId === null
            ? MOTIVO_SENZA_TENTATIVO
            : "Carica subito la fattura sull'ordine Metro, senza aspettare il giro automatico"
        }
      >
        {busy === "metro" ? "Carico…" : "Carica su Metro"}
      </button>
      {esito && !esito.completato && (
        // Tinta neutra, MAI rossa: è lo stato normale di una fattura emessa
        // prima che Metro spedisca. Un badge d'errore qui manderebbe a cercare
        // un guasto che non c'è.
        <span
          className="shrink-0 rounded bg-paper-2 px-1.5 py-px text-[10.5px] font-medium text-ink-2"
          title={`${esito.descrizione}. Metro non ha ancora marcato spedite tutte le righe dell'ordine: il giro automatico riprova da solo, oppure ripremi «Carica su Metro» più tardi.`}
        >
          in attesa di spedizione
        </span>
      )}
    </>
  );
}
